import { Container } from 'components/Container/Container'
import { DEFAULT_PAGE } from 'components/Table/constants'
import { TOptionsWithValueLabelResponse } from 'constants/types'
import axios, { CancelTokenSource } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getCommunityInfo } from 'redux/store/chapterInfo/getters'
import { setLoading } from 'redux/store/common/slice'
import { getUser } from 'redux/store/user/getters'
import {
  getAdditionalValuesSummary,
  getAdditionalValuesTypesList,
  getExpectedSavingsByCategoryRequest,
  getOngoing,
  getSavings,
  getSavingsSummaryList
} from './api'
import { Charts } from './Charts/Charts'
import { TOngoingItem } from './Charts/types'
import { TExpectedSavingsByCategoryWidget } from './ExpectedSavingsByCategory/types'
import { AppliedFiltersBlock } from './Filters/AppliedFiltersBlock'
import { FiltersButton } from './Filters/FiltersButton'
import { FiltersModal } from './Filters/FiltersModal'
import { useDashboardFilterContext } from './Providers/DashboardFilterContendProvider'
import { DashboardTable } from './Table/DashboardTable'
import {
  TAdditionalValuesWidget,
  TCommonTableData,
  TDashboardParamsRequestData,
  TSavingsResponce
} from './types'
import './styles.scss'

type DashboardContentProps = {
  healthSystem: string
}

export const DashboardContent = ({ healthSystem }: DashboardContentProps) => {
  const {
    state: { updateFilters, hideFilter },
    actions: { setFiltersUpdated }
  } = useDashboardFilterContext()
  const currentUser = useSelector(getUser)
  const communityInfo = useSelector(getCommunityInfo)
  const dispatch = useDispatch()

  const [savings, setSavings] = useState<TSavingsResponce>({
    target_sum: '',
    progress: 0,
    inaccurate_data_warning: false,
    total_expected_savings: ''
  })
  const [ongoing, setOngoing] = useState<TOngoingItem | null>(null)
  const [expectedSavingsByCategory, setExpectedSavingsByCategory] =
    useState<TExpectedSavingsByCategoryWidget>([])
  const [additionalValues, setAdditionalValues] =
    useState<TAdditionalValuesWidget>({})
  const [additionalValuesTypes, setAdditionalValuesTypes] = useState<
    TOptionsWithValueLabelResponse[]
  >([])

  const [tableData, setTableData] = useState<TCommonTableData[]>([])
  const [sortParams, setSortParams] = useState<TDashboardParamsRequestData>()
  const [firstLoad, setFirstLoad] = useState(true)
  const [totalNumber, setTotalNumber] = useState(0)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })

  const savingsTokenRef = useRef<CancelTokenSource>()
  const ongoingTokenRef = useRef<CancelTokenSource>()
  const expectedSavingsTokenRef = useRef<CancelTokenSource>()
  const additionalValuesSummaryTokenRef = useRef<CancelTokenSource>()
  const additionalValuesTypesTokenRef = useRef<CancelTokenSource>()
  const tableDataTokenRef = useRef<CancelTokenSource>()

  const cancelAllWidgetRequests = useCallback(() => {
    savingsTokenRef.current?.cancel()
    ongoingTokenRef.current?.cancel()
    expectedSavingsTokenRef.current?.cancel()
    additionalValuesSummaryTokenRef.current?.cancel()
    additionalValuesTypesTokenRef.current?.cancel()
    tableDataTokenRef.current?.cancel()
  }, [])

  const fetchChartData = useCallback(() => {
    savingsTokenRef.current = axios.CancelToken.source()
    getSavings({
      params: {
        health_system: healthSystem,
        user: currentUser.uuid
      },
      cancelToken: savingsTokenRef.current.token
    }).then((res) => setSavings(res?.data))

    ongoingTokenRef.current = axios.CancelToken.source()
    getOngoing({ cancelToken: ongoingTokenRef.current.token }).then((res) =>
      setOngoing(res?.data)
    )
  }, [healthSystem, currentUser?.uuid])

  const fetchExpectedSavings = useCallback(() => {
    expectedSavingsTokenRef.current = axios.CancelToken.source()
    getExpectedSavingsByCategoryRequest({
      cancelToken: expectedSavingsTokenRef.current.token
    })
      .then((resp) => {
        setExpectedSavingsByCategory(
          Object.entries(resp.data).sort((a, b) => b[1] - a[1])
        )
      })
      .catch((e) => {
        if (!axios.isCancel(e)) console.error(e)
      })
  }, [])

  const fetchAdditionalValues = useCallback(() => {
    additionalValuesSummaryTokenRef.current = axios.CancelToken.source()
    getAdditionalValuesSummary({
      cancelToken: additionalValuesSummaryTokenRef.current.token
    })
      .then((resp) => setAdditionalValues(resp.data))
      .catch((e) => {
        if (!axios.isCancel(e)) console.error(e)
      })

    additionalValuesTypesTokenRef.current = axios.CancelToken.source()
    getAdditionalValuesTypesList({
      cancelToken: additionalValuesTypesTokenRef.current.token
    }).then((res) => {
      setAdditionalValuesTypes(
        res.data.results.map((option) => ({
          label: option.value,
          value: option.id
        }))
      )
    })
  }, [])

  const getTableData = useCallback(
    ({ sortField, sortOrder, page }: TDashboardParamsRequestData) => {
      dispatch(setLoading(true))
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
      const params: Record<string, unknown> = {}
      if (sortField) {
        if (sortOrder) {
          params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
        }
        setSortParams({
          sortField,
          sortOrder
        })
      }
      const dataPage = page ?? pageInfo
      params.limit = dataPage.pageSize
      params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
      tableDataTokenRef.current = axios.CancelToken.source()

      getSavingsSummaryList({
        params,
        cancelToken: tableDataTokenRef.current?.token
      })
        .then((resp) => {
          if (!resp.data?.results) {
            return
          }
          setTableData(
            resp.data.results.map((i) => ({
              ...i,
              key: i.uuid
            }))
          )
          setTotalNumber(resp.data.count)
          if (firstLoad) {
            setFirstLoad(false)
          }
        })
        .finally(() => dispatch(setLoading(false)))
    },
    [dispatch, firstLoad, pageInfo]
  )

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      page
    })
    setPageInfo(page)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      page
    })
    setPageInfo(page)
  }

  const fetchWidgetsData = useCallback(() => {
    cancelAllWidgetRequests()
    fetchChartData()
    fetchExpectedSavings()
    fetchAdditionalValues()

    const newPageInfo = { ...pageInfo, pageNumber: 1 }
    setPageInfo(newPageInfo)
    getTableData({ ...sortParams, page: newPageInfo })
    setFiltersUpdated()
  }, [
    cancelAllWidgetRequests,
    fetchChartData,
    fetchExpectedSavings,
    fetchAdditionalValues,
    pageInfo,
    getTableData,
    sortParams,
    setFiltersUpdated
  ])

  useEffect(() => {
    if (!hideFilter) return
    fetchWidgetsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthSystem, currentUser])

  useEffect(() => {
    if (hideFilter || !updateFilters) return
    fetchWidgetsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateFilters])

  useEffect(() => {
    return () => {
      cancelAllWidgetRequests()
    }
  }, [cancelAllWidgetRequests])

  return (
    <Container
      title="Dashboard"
      subtitle={communityInfo?.name}
      actions={<FiltersButton />}
    >
      <div className="my-projects-dashboard-selectors" />
      <AppliedFiltersBlock />
      <Charts
        savings={savings}
        ongoing={ongoing}
        expectedSavingsByCategory={expectedSavingsByCategory}
        additionalValues={additionalValues}
        additionalValuesTypes={additionalValuesTypes}
      />
      <Container className="rfp-create-survey">
        <DashboardTable
          tableData={tableData}
          firstLoad={firstLoad}
          totalNumber={totalNumber}
          pageInfo={pageInfo}
          onTableChange={handleTableChange}
          onChangePageSize={handleChangePageSize}
        />
      </Container>
      <FiltersModal />
    </Container>
  )
}
